import { AiCreditTooltip } from '../../components/ui/AiCreditTooltip';
import { DialogContent } from '../../components/ui/DialogContent';
import {
  Alert,
  Button,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import { DialogHeader } from '../../components/ui/DialogHeader';
import { ResponsiveDialog } from '../../components/ui/ResponsiveDialog';
import { RapidCaptureRecord } from './rapidCaptureTypes';
import { useFeedback } from '../feedback/FeedbackProvider';
import { Item } from '../../domain/item';
import { captureActivityPresentation } from './captureActivityPresentation';
import { CaptureActivityThumbnail } from './CaptureActivityThumbnail';

export function RapidCaptureQueueDialog({
  open,
  records,
  items,
  online,
  onClose,
  onRetry,
  onDiscard,
  onSaveWithoutKeywords,
  onOpenItem,
}: {
  open: boolean;
  records: RapidCaptureRecord[];
  items: Item[];
  online: boolean;
  onClose: () => void;
  onRetry: (operationId: string) => Promise<void>;
  onDiscard: (operationId: string) => Promise<void>;
  onSaveWithoutKeywords?: (operationId: string) => Promise<void>;
  onOpenItem: (item: Item) => void;
}) {
  const { confirm, notify } = useFeedback();
  const itemsById = new Map(items.map((item) => [item.id, item]));
  return (
    <ResponsiveDialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      aria-labelledby="rapid-queue-title"
    >
      <DialogHeader
        id="rapid-queue-title"
        title="Capture queue"
        onClose={onClose}
      />
      <DialogContent>
        {!records.length ? (
          <Typography sx={{ py: 3 }}>There is no upload activity.</Typography>
        ) : (
          <List disablePadding>
            {records.map((record) => {
              const item = itemsById.get(record.itemId);
              const presentation = captureActivityPresentation(
                record,
                online,
                item,
              );
              return (
                <ListItem
                  key={record.operationId}
                  divider
                  alignItems="flex-start"
                  sx={{ gap: 1.5, px: 0, py: 1.5, flexWrap: 'wrap' }}
                >
                  <CaptureActivityThumbnail
                    image={record.image}
                    alt={`Captured item: ${record.keywords.trim() || 'Unnamed'}`}
                  />
                  <ListItemText
                    primary={record.keywords.trim() || 'Unnamed'}
                    secondary={
                      <>
                        <Typography
                          component="span"
                          variant="body2"
                          color={
                            presentation.severity === 'warning'
                              ? 'warning.main'
                              : 'text.secondary'
                          }
                        >
                          {presentation.label}
                        </Typography>
                        <Typography
                          component="span"
                          variant="caption"
                          display="block"
                          color="text.secondary"
                        >
                          {record.location}
                        </Typography>
                        {presentation.detail && (
                          <Typography
                            component="span"
                            variant="caption"
                            display="block"
                            color="text.secondary"
                          >
                            {presentation.detail}
                          </Typography>
                        )}
                      </>
                    }
                    sx={{ my: 0, minWidth: 0 }}
                  />
                  <Stack spacing={0.25} alignItems="flex-end">
                    {presentation.canOpenItem && item && (
                      <Button size="small" onClick={() => onOpenItem(item)}>
                        Open item
                      </Button>
                    )}
                    {presentation.canRetry && (
                      <Stack direction="row" alignItems="center">
                        <Button
                          size="small"
                          onClick={async () => {
                            if (
                              (record as { schemaVersion: number })
                                .schemaVersion !== 5 &&
                              !(await confirm(
                                'Check whether this item was saved, then restart its photo if needed? Its AI keywords will not run again. Any uncertain AI charge remains under reconciliation.',
                                'Restart older capture',
                              ))
                            )
                              return;
                            await onRetry(record.operationId);
                          }}
                        >
                          {presentation.retryLabel || 'Retry'}
                        </Button>
                        {record.analyzeImage && (
                          <AiCreditTooltip
                            authority={
                              online
                                ? {
                                    userId: record.userId,
                                    collectionId: record.collectionId,
                                    membershipVersion: record.membershipVersion,
                                    compatibilityMode: record.compatibilityMode,
                                  }
                                : undefined
                            }
                            showPreviouslyAnalysedNote={false}
                          />
                        )}
                      </Stack>
                    )}
                    {record.schemaVersion === 5 &&
                      record.state === 'failed' &&
                      record.analyzeImage &&
                      online &&
                      onSaveWithoutKeywords && (
                        <Button
                          size="small"
                          onClick={async () => {
                            try {
                              await onSaveWithoutKeywords(record.operationId);
                            } catch (error) {
                              notify(
                                error instanceof Error
                                  ? error.message
                                  : 'Save failed. The capture is retained.',
                                'error',
                              );
                            }
                          }}
                        >
                          Save without keywords
                        </Button>
                      )}
                    {presentation.canDiscard && (
                      <Button
                        size="small"
                        color="error"
                        onClick={async () => {
                          if (
                            await confirm(
                              'Discard this capture? It will not be added to inventory. Completed AI work may still use credits.',
                              'Discard queued item',
                            )
                          ) {
                            try {
                              await onDiscard(record.operationId);
                            } catch (error) {
                              notify(
                                error instanceof Error
                                  ? error.message
                                  : 'Discard failed. The capture is retained.',
                                'error',
                              );
                            }
                          }
                        }}
                      >
                        Discard
                      </Button>
                    )}
                  </Stack>
                </ListItem>
              );
            })}
          </List>
        )}
        {records.some(({ state }) => state === 'failed') && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            Failed captures stay in the queue until you retry, save without
            keywords, or discard them.
          </Alert>
        )}
      </DialogContent>
    </ResponsiveDialog>
  );
}
